/** Pure admission state for the sole LIVE tool. Provider transcripts are keyed
 * by the committed audio item, never by arrival order or model arguments. */
export const MIRROR_CURRENT_TURN_TOOL = "mirror_current_turn";
export const LIVE_TOOL_TRANSCRIPT_WAIT_MS = 5000;
export const LIVE_TOOL_REQUEST_TIMEOUT_MS = 30000;
const CAPACITY = 200;
const id = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9_-]{1,200}$/.test(value);
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export interface MirrorLiveAppHandoff {
  readonly type: "app_handoff";
  readonly reason: "app_review" | "app_only" | "native_required";
  readonly url: "https://www.aivyv.com/mirror";
  readonly executed: false;
  readonly receipt: null;
}
export interface MirrorLiveToolHandoff {
  readonly handoff: MirrorLiveAppHandoff;
  readonly response: string;
  readonly transcript: string;
  readonly ownerId: string;
  readonly authSessionId: string;
  readonly liveSessionId: string;
  readonly runNonce: string;
  readonly sessionId: string;
  readonly sourceItemId: string;
  readonly callId: string;
}
export interface MirrorLiveCurrentTurnCall {
  readonly epoch: number;
  readonly responseId: string;
  readonly sourceItemId: string;
  readonly callId: string;
}
interface Association { epoch: number; sourceItemId: string | null }
export interface MirrorLiveCurrentTurnState {
  readonly epoch: number;
  readonly currentSource: string | null;
  readonly unbound: readonly string[];
  readonly committed: ReadonlySet<string>;
  readonly responses: ReadonlyMap<string, Association>;
  readonly active: ReadonlySet<string>;
  readonly transcripts: ReadonlyMap<string, string | null>;
  readonly calls: ReadonlySet<string>;
}
export function createMirrorLiveCurrentTurnState(): MirrorLiveCurrentTurnState {
  return { epoch: 0, currentSource: null, unbound: [], committed: new Set(),
    responses: new Map(), active: new Set(), transcripts: new Map(), calls: new Set() };
}
export function invalidateMirrorLiveCurrentTurn(state: MirrorLiveCurrentTurnState): MirrorLiveCurrentTurnState {
  return { ...state, epoch: state.epoch + 1, currentSource: null, unbound: [], active: new Set() };
}
export function isMirrorLiveCurrentTurnCallCurrent(state: MirrorLiveCurrentTurnState, call: MirrorLiveCurrentTurnCall): boolean {
  const association = state.responses.get(call.responseId);
  return state.epoch === call.epoch && state.currentSource === call.sourceItemId
    && association?.epoch === call.epoch && association.sourceItemId === call.sourceItemId;
}
function emptyArguments(value: unknown): boolean {
  if (typeof value !== "string" || value.length > 100) return false;
  try { const parsed: unknown = JSON.parse(value);
    return object(parsed) && Object.getPrototypeOf(parsed) === Object.prototype && Object.keys(parsed).length === 0;
  } catch { return false; }
}
/** Each transition returns new collections; it performs no IO or callbacks. */
export function advanceMirrorLiveCurrentTurn(
  state: MirrorLiveCurrentTurnState, event: unknown, maxTranscriptChars = 4000,
): { state: MirrorLiveCurrentTurnState; call?: MirrorLiveCurrentTurnCall } {
  if (!object(event)) return { state };
  if (event.type === "input_audio_buffer.speech_started") return { state: invalidateMirrorLiveCurrentTurn(state) };
  if (event.type === "input_audio_buffer.committed") {
    if (!id(event.item_id) || state.committed.size >= CAPACITY) return { state: invalidateMirrorLiveCurrentTurn(state) };
    if (state.committed.has(event.item_id)) return { state };
    return { state: { ...state, epoch: state.epoch + 1, currentSource: event.item_id,
      unbound: [...state.unbound, event.item_id], committed: new Set([...state.committed, event.item_id]) } };
  }
  if (event.type === "conversation.item.input_audio_transcription.completed"
    || event.type === "conversation.item.input_audio_transcription.failed") {
    if (!id(event.item_id) || (!state.transcripts.has(event.item_id) && state.transcripts.size >= CAPACITY)) return { state };
    const raw = event.transcript, text = typeof raw === "string" ? raw.trim() : "";
    const valid = event.type === "conversation.item.input_audio_transcription.completed"
      && (event.content_index === undefined || event.content_index === 0) && text.length > 0 && text.length <= maxTranscriptChars;
    const value = valid ? text : null, prior = state.transcripts.get(event.item_id);
    const conflict = state.transcripts.has(event.item_id) && prior !== value;
    const transcripts = new Map(state.transcripts); transcripts.set(event.item_id, conflict ? null : value);
    return { state: { ...state, transcripts,
      ...(conflict && state.currentSource === event.item_id ? { epoch: state.epoch + 1 } : {}) } };
  }
  if (event.type === "response.created") {
    const response = event.response;
    if (!object(response) || !id(response.id) || state.responses.size >= CAPACITY)
      return { state: invalidateMirrorLiveCurrentTurn(state) };
    if (state.responses.has(response.id)) return { state };
    const overlap = state.active.size > 0, epoch = overlap ? state.epoch + 1 : state.epoch;
    const sourceItemId = !overlap && state.unbound.length === 1 && state.unbound[0] === state.currentSource
      ? state.unbound[0] : null;
    const responses = new Map(state.responses); responses.set(response.id, { epoch, sourceItemId });
    return { state: { ...state, epoch, unbound: [], responses, active: new Set([...state.active, response.id]) } };
  }
  if (event.type !== "response.done") return { state };
  const response = event.response;
  if (!object(response) || !id(response.id)) return { state: invalidateMirrorLiveCurrentTurn(state) };
  const responseId = response.id;
  const association = state.responses.get(responseId), wasActive = state.active.has(responseId);
  const active = new Set(state.active); active.delete(responseId);
  const calls = new Set(state.calls), output = response.output;
  let repeated = false;
  if (Array.isArray(output)) for (const item of output) {
    if (object(item) && item.type === "function_call" && id(item.call_id)) {
      repeated ||= calls.has(item.call_id);
      if (calls.size < CAPACITY) calls.add(item.call_id); else repeated = true;
    }
  }
  const next = { ...state, active, calls };
  if (!wasActive || !association || association.sourceItemId === null || response.status !== "completed"
    || association.epoch !== state.epoch || association.sourceItemId !== state.currentSource
    || !Array.isArray(output) || output.length !== 1 || repeated) return { state: next };
  const item = output[0];
  if (!object(item) || item.type !== "function_call" || item.name !== MIRROR_CURRENT_TURN_TOOL
    || !id(item.call_id) || (item.status !== undefined && item.status !== "completed") || !emptyArguments(item.arguments)) return { state: next };
  const call = { epoch: state.epoch, responseId, sourceItemId: association.sourceItemId, callId: item.call_id };
  return isMirrorLiveCurrentTurnCallCurrent(next, call) ? { state: next, call } : { state: next };
}
